import { API_URLS } from '@helpers/api';
import { InterfaceIcon, getSvgIcon } from '@helpers/icons';
import { clamp } from '@helpers/utils';
import { useApi } from '@hooks/useApi';
import { useDebounce } from '@hooks/useDebounce';
import { useForm, validateNotWhitespaceOnly } from '@hooks/useForm';
import { useOutsideClick } from '@hooks/useOutsideClick';
import qs from 'qs';
import * as React from 'react';
import S from './Searchbox.styles';
const Searchbox = (props) => {
    const { onSubmit, useReactHookFormReturn: { watch, register, setValue, setFocus, formState: { errors }, }, status, } = useForm({
        defaultValues: { searchTerm: props.searchTerm },
        endpoint: API_URLS.searchSubmit,
        mode: 'json',
    });
    const { searchTerm: searchValue } = watch();
    const [searchTerm, setSearchTerm] = React.useState(props.searchTerm ?? '');
    const [showSuggestions, setShowSuggestions] = React.useState(false);
    const [selectedTerm, setSelectedTerm] = React.useState('');
    React.useEffect(() => {
        debouncedUpdateSearchTerm();
    }, [searchValue]);
    const updateSearchTerm = () => {
        if (searchValue &&
            searchValue.length > 1 &&
            searchTerm !== searchValue &&
            searchValue !== selectedTerm) {
            setSearchTerm(searchValue);
            setShowSuggestions(true);
        }
        else {
            setShowSuggestions(false);
        }
    };
    const debouncedUpdateSearchTerm = useDebounce(updateSearchTerm, 200);
    React.useEffect(() => {
        setSearchTerm(props.searchTerm ?? '');
        setValue('searchTerm', props.searchTerm ?? '');
    }, [props.searchTerm]);
    const result = useApi({
        url: `${API_URLS.searchTypeahead}?${qs.stringify({
            searchTerm,
            cultureCode: props.cultureCode,
        })}`,
        validate: () => !props.noTypeahead && !!searchTerm && searchTerm !== props.searchTerm,
    });
    const itemsRef = React.useRef([]);
    const containerRef = useOutsideClick(() => setShowSuggestions(false));
    return (React.createElement(S.Container, { ref: containerRef },
        React.createElement(S.Form, { onSubmit: props.onSubmit !== undefined
                ? (event) => {
                    event.preventDefault();
                    props.onSubmit?.(searchTerm);
                    showSuggestions && setShowSuggestions(false);
                }
                : props.submitUrl
                    ? (event) => {
                        event.preventDefault();
                        window.location.href = `${props.submitUrl}?${qs.stringify({
                            searchTerm: searchTerm,
                            cultureCode: props.cultureCode,
                        })}`;
                    }
                    : onSubmit },
            React.createElement(S.Label, { id: `${props.idPrefix ?? ''}-searchbox-label` }, props.label),
            React.createElement(S.Input, { type: "search", placeholder: props.placeholder, role: "combobox", "aria-autocomplete": "list", "aria-expanded": showSuggestions && !!result.data && result.data.suggestions.length > 0, "aria-controls": `${props.idPrefix ?? ''}-typeahead-listbox`, "aria-labelledby": `${props.idPrefix ?? ''}-searchbox-label`, ...register('searchTerm', {
                    required: props.translations?.['global.search.validation'] ?? '',
                    validate: (value) => validateNotWhitespaceOnly(value, props.translations?.['global.search.validation'] ?? ''),
                }), "aria-invalid": !!errors.searchTerm, "aria-errormessage": `${props.idPrefix ?? ''}-searchbox-error`, onKeyDown: (event) => {
                    switch (event.key) {
                        case 'ArrowDown':
                            if (itemsRef.current.length > 0) {
                                itemsRef.current[0]?.focus();
                            }
                            break;
                        case 'Escape':
                            setShowSuggestions(false);
                            break;
                    }
                }, "aria-disabled": status === 'pending', autoComplete: "off" }),
            errors.searchTerm && React.createElement(S.Label, { id: `${props.idPrefix ?? ''}-searchbox-error`, as: "span" }),
            React.createElement(S.Button, { type: "submit", "aria-label": props.buttonLabel, "aria-disabled": status === 'pending' }, getSvgIcon(InterfaceIcon.Search))),
        React.createElement(S.Suggestions, { id: `${props.idPrefix ?? ''}-typeahead-listbox`, "aria-hidden": !result.data || result.data.suggestions.length === 0, tabIndex: showSuggestions ? -1 : undefined }, !!result.data && (React.createElement(React.Fragment, null, result.data.suggestions.map((suggestion, i) => (React.createElement("li", { key: suggestion.id }, suggestion.url !== undefined ? (React.createElement(S.AutosuggestOption, { ref: (el) => (itemsRef.current[i] = el), as: "a", href: suggestion.url, onKeyDown: (e) => onKeyPress(e, i, (result.data?.suggestions).length - 1), tabIndex: showSuggestions ? -1 : undefined }, suggestion.value)) : (React.createElement(S.AutosuggestOption, { ref: (el) => (itemsRef.current[i] = el), onClick: (e) => {
                e.preventDefault();
                e.stopPropagation();
                setSelectedTerm(suggestion.value);
                setValue('searchTerm', suggestion.value);
            }, onKeyDown: (e) => onKeyPress(e, i, (result.data?.suggestions).length - 1), tabIndex: showSuggestions ? -1 : undefined }, suggestion.value))))))))));
    function onKeyPress(event, i, maxIndex) {
        const nextIndex = i === maxIndex ? 0 : clamp(i + 1, maxIndex);
        const prevIndex = i === 0 ? maxIndex : clamp(i - 1, maxIndex);
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                event.stopPropagation();
                itemsRef.current[nextIndex]?.focus();
                break;
            case 'ArrowUp':
                event.preventDefault();
                event.stopPropagation();
                itemsRef.current[prevIndex]?.focus();
                break;
            case 'Escape':
                setShowSuggestions(false);
                setFocus('searchTerm');
                break;
            default:
                break;
        }
    }
};
export default Searchbox;
